import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ROUTES } from '@/app/routes';
import { safeMediaSrc } from '@/lib/mediaUrl';
import { userTypeLabel, userStatusLabel } from '@/lib/enums';
import type {
  AdminPostManagerDetailResponseDTO,
  AdminUserDetailsResponseDTO,
} from '@/api/generated/model';

interface CreatorInfoCardProps {
  detail: AdminPostManagerDetailResponseDTO;
  creator: AdminUserDetailsResponseDTO | undefined;
  creatorId: number | null;
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex justify-between gap-4 text-sm">
      <span className="shrink-0 text-muted-foreground">{label}</span>
      <span className="min-w-0 truncate text-right">{children}</span>
    </div>
  );
}

// Creator Information pane (doc 08 §4.2, §4.10). The admin detail DTO carries the real author via
// `creator*` fields; the (tolerated-failure) user-detail fetch enriches it with the creator's user
// type + account status. "User Profile" cross-navigates to the author's user detail page.
export function CreatorInfoCard({ detail, creator, creatorId }: CreatorInfoCardProps) {
  const navigate = useNavigate();

  const displayName = detail.creatorDisplayName ?? creator?.displayName ?? 'Unknown';
  const handle = detail.creatorUName ?? creator?.uName;
  const avatar = safeMediaSrc(detail.creatorAvatarImage ?? creator?.avatarImage);

  const userType = creator?.userType;
  const userStatus = creator?.userStatus;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">Creator Information</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-3">
          <Avatar>
            <AvatarImage src={avatar ?? undefined} alt={displayName} />
            <AvatarFallback>{displayName.slice(0, 2).toUpperCase()}</AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <div className="truncate text-sm font-medium">{displayName}</div>
            {handle && <div className="truncate text-xs text-muted-foreground">@{handle}</div>}
          </div>
        </div>

        <div className="space-y-1.5">
          <Row label="User Type">{userType ? userTypeLabel(userType) : '—'}</Row>
          <Row label="Account Status">{userStatus ? userStatusLabel(userStatus) : '—'}</Row>
        </div>

        <Button
          variant="outline"
          size="sm"
          className="w-full"
          disabled={creatorId == null}
          onClick={() => {
            if (creatorId != null) void navigate(ROUTES.userDetails(String(creatorId)));
          }}
        >
          User Profile
        </Button>
      </CardContent>
    </Card>
  );
}
